import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import { flattenAgenda, TAX_I18N } from "@/pages/dashboard/dashboardAlertsModel";
import { getTaxLabelKey, getAuthorityLabelKey } from "@/lib/fiscalAgendaDisplay";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import type { FiscalAgendaObligation } from "@/services/fiscalService";
import type {
  TimelineEvent,
  TimelineRowAction,
} from "@/pages/estado-fiscal/Proximos60Dias";

export interface UseUpcomingObligationsTimelineOptions {
  /** Source agenda grouped by yyyy-MM, as returned by getFiscalAgenda. */
  agendaGrouped: Record<string, FiscalAgendaObligation[]> | undefined;
  /** Max rows to return. Default 8. */
  limit?: number;
  /** Window in days from today. Default 60. */
  windowDays?: number;
  /** When set, fills the IVA row's `amount` with this DGI total (used on the estado-fiscal page). */
  ivaDgiTotal?: number;
  /** When set, marks the IVA row's amount as credit when ivaDgiTotal < 0. */
  ivaObligationId?: string | null;
  /**
   * Style toggle. "rich" uses the relative-date sub copy ("Vence hoy", "Vence en N días"…)
   * and matches estado-fiscal's tax label key. "agenda" uses the absolute display date.
   * Both pages render the same widget — this just changes the sub-line text and tax label source.
   */
  variant?: "rich" | "agenda";
  /**
   * Optional callback to derive inline row actions from each obligation. When returns
   * a non-empty array, the timeline row renders buttons instead of the static amount.
   */
  buildActions?: (obligation: FiscalAgendaObligation) => TimelineRowAction[] | undefined;
}

/**
 * Derive the list of TimelineEvent rows the <Proximos60Dias /> widget renders.
 * Centralises the next-60-days / sort / IVA-credit-amount logic so the dashboard
 * and estado-fiscal page can't drift visually or numerically.
 */
export function useUpcomingObligationsTimeline({
  agendaGrouped,
  limit = 8,
  windowDays = 60,
  ivaDgiTotal,
  ivaObligationId = null,
  variant = "rich",
  buildActions,
}: UseUpcomingObligationsTimelineOptions): TimelineEvent[] {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    if (!agendaGrouped) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() + windowDays);

    const upcoming = flattenAgenda(agendaGrouped).filter((o) => {
      if (o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due >= today && due <= cutoff;
    });

    const monthAbbrevOf = (d: Date): string =>
      new Intl.DateTimeFormat(i18n.language, { month: "short" })
        .format(d)
        .replace(/\./g, "")
        .toUpperCase()
        .slice(0, 3);

    return upcoming.slice(0, limit).map((o): TimelineEvent => {
      const due = parseLocalDateOnly(o.dueDate);
      const taxLabel =
        variant === "rich"
          ? t(getTaxLabelKey(o.taxType), o.taxType)
          : t(TAX_I18N[o.taxType] ?? o.taxType);
      const authorityLabel = t(getAuthorityLabelKey(o.authority), o.authority);
      const days = Math.ceil(
        (due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
      );
      const isIva = o.taxType === "IVA";
      const isCredit =
        isIva &&
        typeof ivaDgiTotal === "number" &&
        ivaDgiTotal < 0 &&
        o.id === ivaObligationId;

      let amount = "—";
      if (isIva && typeof ivaDgiTotal === "number" && ivaDgiTotal !== 0) {
        const sign = ivaDgiTotal < 0 ? "−" : "";
        amount = `${sign}${formatDashboardCurrency(Math.abs(ivaDgiTotal))} UYU`;
      }

      const sub =
        variant === "rich"
          ? t("estadoFiscal.timelineDueOn", "Vence {{date}}", {
              date: formatDisplayDate(o.dueDate, {
                locale: i18n.language,
                useCalendarDate: true,
              }),
            })
          : // `days` is never negative here: the filter above keeps only
            // obligations with `due >= today`, both at local midnight. Overdue
            // ones are surfaced by DashboardHeaderSummary's separate
            // nextObligation card, not by this "próximos 60 días" widget.
            days === 0
            ? t("dashboard.headerSummary.dueToday")
            : days === 1
              ? t("dashboard.headerSummary.dueTomorrow")
              : t("dashboard.headerSummary.dueInDays", { count: days });

      const actions = buildActions?.(o);

      return {
        id: o.id,
        when: `${String(due.getDate()).padStart(2, "0")} ${monthAbbrevOf(due)}`,
        titleLead: taxLabel,
        titleSuffix:
          variant === "rich"
            ? o.taxType !== "IVA"
              ? authorityLabel
              : undefined
            : o.authority !== o.taxType
              ? `(${o.authority})`
              : undefined,
        sub,
        amount,
        credit: isCredit,
        actions: actions && actions.length > 0 ? actions : undefined,
      };
    });
  }, [
    agendaGrouped,
    buildActions,
    i18n.language,
    ivaDgiTotal,
    ivaObligationId,
    limit,
    t,
    variant,
    windowDays,
  ]);
}
